import { Auth } from "@aws-amplify/auth";
import { User } from "./authTypes";
import {
  getUserData,
  refreshCurrentSession,
  setCogitoCredentials,
} from "./config";
import { setUser } from "./user";

const SESSION_DURATION = 900000; // 15 Mints for inactivity logout
const MAX_SESSION_TIME = 43200000; // 12 hours maximum session time in milliseconds

let inactivityTimer: NodeJS.Timeout | null = null; // Timer for inactivity logout
let sessionRefreshInterval: NodeJS.Timeout | null = null; // Interval to refresh session

// FederatedSignIn is added for compatibility with existing code
export const federatedSignIn = async () => {
  await Auth.federatedSignIn(); // Redirect to federated sign-in
};

// Logout the user due to inactivity
const logoutUser = async () => {
  try {
    clearTimers(); // Clear all timers and intervals before logout
    await Auth.signOut(); // Clear credentials
    window.location.href = "/login"; // Redirect to login
  } catch (error) {
    console.error("Error logging out user:", error);
  }
};

// Clear all timers and intervals
const clearTimers = () => {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  if (sessionRefreshInterval) clearInterval(sessionRefreshInterval);
};

// Set the inactivity timer to log out user after 15 mints of inactivity
const startInactivityTimer = () => {
  if (inactivityTimer) clearTimeout(inactivityTimer);

  inactivityTimer = setTimeout(() => {
    logoutUser(); // Logout when inactive after 15 mints 
  }, SESSION_DURATION);
};

// Track user activity (e.g., mouse, keyboard, scroll) and reset inactivity timer
const bindUserActivityListeners = () => {
  const resetInactivityTimer = () => {
    startInactivityTimer(); // Restart the timer
  };

  window.addEventListener("mousemove", resetInactivityTimer);
  window.addEventListener("keydown", resetInactivityTimer);
  window.addEventListener("scroll", resetInactivityTimer);
  window.addEventListener("click", resetInactivityTimer);
};

// Refresh session credentials periodically every 55 minutes
const startSessionRefresh = () => {
  const REFRESH_INTERVAL = 55 * 60 * 1000; // 55 minutes in milliseconds
  sessionRefreshInterval = setInterval(() => {
    refreshCurrentSession();
  }, REFRESH_INTERVAL);
};

// Manage session timeout after 12 Hours
const startSessionTimeout = () => {
  setTimeout(() => {
    logoutUser(); // Logout user after 12 Hours
  }, MAX_SESSION_TIME);
};

// Initialize all session-related timers
const initializeSessionManagement = () => {
  startInactivityTimer(); // Start inactivity tracker
  startSessionRefresh(); // Start periodic session refresh logic
  startSessionTimeout(); // Ensure max session time is followed
  bindUserActivityListeners(); // Track user activity
};

// Authenticate the user and initialize session handling
export const authenticate = async (loadContent: () => void) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const { accessKeyId, sessionToken, secretAccessKey, identityId } =
      await Auth.currentCredentials();
    const [factories, currentFactory, username, roles, cognitoRoles] =
      getUserData(user);

    setUser({
      username,
      factoryList: factories,
      factory: currentFactory,
      roles: roles,
      roleInCurrentFactory: roles.get(currentFactory),
      cognitoRoles,
      cognitoRoleInCurrentFactory: roles.get(currentFactory),
      credentials: {
        accessToken: user.signInUserSession.getAccessToken().getJwtToken(),
        idToken: user.signInUserSession.getIdToken().getJwtToken(),
        accessKeyId,
        secretAccessKey,
        sessionToken,
        identityId,
      },
    } as User);

    await setCogitoCredentials(user);
    initializeSessionManagement(); // Start session management logic
    loadContent();
  } catch (error) {
    console.error("Authentication failed, redirecting to login...");
    await federatedSignIn(); // Redirect to federated login
  }
};
